import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { Plant, ArrowRight, Trash, Camera, ChartLineUp, ThermometerHot, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function History() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/scans"),
        api.get("/stats"),
      ]);
      setItems(list.items || []);
      setStats(s);
    } catch (e) {
      toast.error("Couldn't load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (scan_id) => {
    if (!window.confirm("Delete this scan?")) return;
    try {
      await api.delete(`/scans/${scan_id}`);
      setItems((arr) => arr.filter((x) => x.scan_id !== scan_id));
      toast.success("Scan deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const fmtDate = (s) => {
    try {
      return new Date(s).toLocaleString();
    } catch {
      return s;
    }
  };

  return (
    <div className="min-h-screen bg-sand-50" data-testid="history-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono-data text-mute">your field journal</div>
            <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight">Scan history</h1>
          </div>
          <Link to="/scan" className="btn-primary" data-testid="history-new-scan"><Camera size={16} weight="bold" /> New scan</Link>
        </div>

        {stats && (
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            <StatCard label="Total scans" value={stats.total_scans} Icon={ChartLineUp} testid="stat-total" />
            <StatCard label="Healthy" value={stats.healthy_scans} Icon={CheckCircle} testid="stat-healthy" />
            <StatCard label="Climate-driven" value={stats.climate_driven_scans} Icon={ThermometerHot} testid="stat-climate" />
          </div>
        )}

        <div className="mt-10">
          {loading ? (
            <div className="font-mono-data text-mute animate-pulse-soft">loading scans…</div>
          ) : items.length === 0 ? (
            <div className="bg-sand-100 border border-sand-200 rounded-3xl p-12 text-center" data-testid="empty-history">
              <Plant size={48} weight="duotone" className="text-forest mx-auto mb-4" />
              <h3 className="font-display text-3xl">No scans yet</h3>
              <p className="text-mute mt-2">Capture your first plant and we'll build your climate-aware field journal here.</p>
              <Link to="/scan" className="btn-primary mt-6 inline-flex"><Camera size={14} /> Start scanning</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="history-grid">
              {items.map((s) => (
                <ScanCard key={s.scan_id} s={s} fmtDate={fmtDate} onRemove={remove} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, Icon, testid }) {
  return (
    <div className="bg-sand-100 border border-sand-200 rounded-2xl p-5 flex items-center gap-4" data-testid={testid}>
      <div className="w-12 h-12 rounded-2xl bg-forest text-white flex items-center justify-center">
        <Icon size={22} weight="duotone" />
      </div>
      <div>
        <div className="font-mono-data text-mute">{label}</div>
        <div className="font-display text-3xl text-ink leading-none">{value}</div>
      </div>
    </div>
  );
}

function ScanCard({ s, fmtDate, onRemove }) {
  const p = s.primary_diagnosis || {};
  const sev = (p.severity || "mild").toLowerCase();
  const climateRisk = s.climate_contribution?.climate_risk_score;
  return (
    <div className="bg-sand-100 border border-sand-200 rounded-2xl overflow-hidden group" data-testid="history-card">
      <Link to={`/scan/${s.scan_id}`} className="block">
        <div className="aspect-[4/3] bg-ink/5 overflow-hidden relative">
          {s.thumbnail_b64 ? (
            <img
              src={`data:image/jpeg;base64,${s.thumbnail_b64}`}
              alt="scan"
              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-mute"><Plant size={36} weight="duotone" /></div>
          )}
          <span className={`absolute top-3 left-3 tag severity-${sev}`}>{p.severity || "—"}</span>
          <span className="absolute bottom-3 left-3 tag bg-white/90 capitalize">{s.kind || "plant"}</span>
          {climateRisk != null && (
            <span className="absolute top-3 right-3 tag bg-white/90">climate {climateRisk}</span>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-display text-xl text-ink truncate">{p.disorder || p.condition || p.issue || "Inconclusive"}</h3>
            <ArrowRight size={16} weight="bold" className="text-forest opacity-0 group-hover:opacity-100 transition" />
          </div>
          <div className="text-mute text-sm mt-1 truncate">{s.plant_identification?.common_name || s.plant_identification?.soil_type_guess || "—"}</div>
          <div className="mt-3 font-mono-data text-mute text-xs flex items-center justify-between">
            <span>{fmtDate(s.created_at)}</span>
            <span>{s.capture_mode}</span>
          </div>
        </div>
      </Link>
      <div className="px-5 pb-5 -mt-3 flex justify-end">
        <button
          onClick={() => onRemove(s.scan_id)}
          className="text-mute hover:text-terra text-xs inline-flex items-center gap-1"
          data-testid="history-delete-btn"
        >
          <Trash size={12} /> delete
        </button>
      </div>
    </div>
  );
}
